import { formatCursor, parseCursor, parseJson, type Cursor, type EventDto } from "@nabla/api";
import { readSent, type Readout } from "./answers.ts";
import { sameApiOf, type Reach } from "./http.ts";
import { backoff, sseReader } from "./sse.ts";

export type Connection = { readonly state: "conectando" } | { readonly state: "aberta" } | { readonly state: "caida"; readonly since: number };

export interface StreamDeps {
  readonly host: Reach;
  readonly after: (ms: number, run: () => void) => () => void;
  readonly now: () => number;
  readonly applied: () => Cursor;
  readonly onEvent: (event: EventDto) => void;
  readonly onSkipped: (to: Cursor) => void;
  readonly onConnection: (connection: Connection) => void;
  readonly onUnreadable: (reason: string) => void;
  readonly signedOut: () => void;
}

interface Stream {
  start(): void;
  stop(): void;
}

const silence = 45_000;

export function createStream(deps: StreamDeps): Stream {
  let controller: AbortController | undefined;
  let failures = 0;
  let downSince: number | undefined;
  let retryTimer: (() => void) | undefined;
  let deadTimer: (() => void) | undefined;
  let stopped = true;

  const down = () => {
    downSince ??= deps.now();
    deps.onConnection({ state: "caida", since: downSince });
  };

  function fail(): void {
    controller?.abort();
    deadTimer?.();
    if (stopped) return;
    failures += 1;
    down();
    retryTimer?.();
    retryTimer = deps.after(backoff(failures), connect);
  }

  function heard(): void {
    deadTimer?.();
    deadTimer = deps.after(silence, fail);
  }

  async function connect(): Promise<void> {
    retryTimer = undefined;
    const own = new AbortController();
    controller = own;
    if (downSince === undefined) deps.onConnection({ state: "conectando" });
    const resume = formatCursor(deps.applied());
    let response: Response;
    try {
      response = await deps.host.fetch(`/api/eventos?desde=${resume}`, { headers: { Accept: "text/event-stream", "Last-Event-ID": resume }, cache: "no-store", credentials: "same-origin", signal: own.signal });
    } catch {
      if (controller === own) fail();
      return;
    }
    if (!response.ok || response.body === null) return response.status === 401 ? deps.signedOut() : fail();
    const sameApi = sameApiOf(deps.host, response);
    heard();
    const read = sseReader();
    const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        heard();
        for (const message of read(value)) {
          if (message.data === "") {
            const to = message.id === undefined ? undefined : parseCursor(message.id);
            if (to?.ok) deps.onSkipped(to.value);
            continue;
          }
          const event = await eventOf(message.data, sameApi);
          if (!event.ok) {
            deps.onUnreadable(event.detail);
            continue;
          }
          if (event.value.type === "ola") {
            failures = 0;
            downSince = undefined;
            deps.onConnection({ state: "aberta" });
          }
          deps.onEvent(event.value);
        }
      }
    } catch {
    }
    if (controller === own) fail();
  }

  return {
    start() {
      if (!stopped) return;
      stopped = false;
      void connect();
    },
    stop() {
      stopped = true;
      controller?.abort();
      retryTimer?.();
      deadTimer?.();
    },
  };
}

async function eventOf(data: string, sameApi: boolean): Promise<Readout<EventDto>> {
  const json = parseJson(data);
  return json.ok ? readSent(json.value, sameApi, (api) => api.EventDto) : { ok: false, detail: json.reason };
}
